import { FC, useState } from 'react';
import { Popover } from '@headlessui/react';
import { createPortal } from 'react-dom';
import moment from 'moment';
import useMatchMutate from 'hooks/useMatchMutate';

import RejectAppointmentModal from 'components/RejectAppointmentModal/RejectAppointmentModal';
import { IAppointment } from 'interfaces/appointments';
import { datetimeFormat } from 'utils/datetime';
import {
  getAppointmentStartTime,
  getDoctorWorkingTime,
  getHygienistWorkingTime,
} from 'components/TableColumn/utils/getAppointmentTime';
import { getIsDoctorStartBeforeHygienist } from 'pages/SchedulePage/TimeGrid/CardDetail/utils/getPractitionerDetail';
import {
  calculateAge,
  capitalizeAvatarName,
  concatFullName,
} from 'utils/common';
import { formatFullPhoneNumber } from 'utils/formatPhoneNumber';
import { ReactComponent as CloseIcon } from 'assets/icons/close.svg';
import { ReactComponent as MailOutlineIcon } from 'assets/icons/mail_outline.svg';
import { ReactComponent as PhoneIphoneIcon } from 'assets/icons/phone_iphone.svg';
import { ReactComponent as BirthdayIcon } from 'assets/icons/cake.svg';
import { ReactComponent as TimeIcon } from 'assets/icons/time-line.svg';
import { ReactComponent as ServiceIcon } from 'assets/icons/service.svg';
import { ReactComponent as PractitionerIcon } from 'assets/icons/practitioners-icon.svg';
import getPractitionerName from 'utils/getPractitionerName';
import { getDentistWorkingHour } from 'utils/getDentistWorkingHour';

interface RebookingPopoverProps {
  appointments: IAppointment[];
  updateDashboardUI?: () => Promise<void>;
}

const RebookingPopover: FC<RebookingPopoverProps> = ({
  appointments,
  updateDashboardUI,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [rejectAppointmentId, setRejectAppointmentId] = useState<string | null>(
    null
  );
  const matchMutate = useMatchMutate();

  const handleCloseModal = async () => {
    setIsModalOpen(false);
    if (updateDashboardUI) {
      await updateDashboardUI();
    }
  };

  const renderDentistHygienistInfo = (data: any) => {
    if (data.linkedAppointment) {
      return (
        <div className="flex gap-x-1">
          <PractitionerIcon className="w-2 h-2 all-child:all-child:fill-mid-grey" />
          <div
            className={`flex flex-col ${
              getIsDoctorStartBeforeHygienist(data) ? '' : 'flex-col-reverse'
            } `}
          >
            <div>
              <div className="flex gap-x-0.5">
                <span>Doctor: </span>
                <span>
                  {getPractitionerName(data.linkedAppointment.doctor)} (
                  {getDoctorWorkingTime(data)})
                </span>
              </div>

              <div className="flex gap-x-0.5">
                <span>Operatory: </span>
                <span>{data.linkedAppointment.operatory.name}</span>
              </div>
            </div>

            <div className="mb-[15px]">
              <div className="flex gap-x-0.5">
                <span>Hygienist: </span>
                <span>
                  {getPractitionerName(data.doctor)} (
                  {getHygienistWorkingTime(data)})
                </span>
              </div>

              <div className="flex gap-x-0.5">
                <span>Operatory: </span>
                <span>{data.operatory.name}</span>
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (data.isSplitScheduling) {
      return (
        <div className="flex gap-x-1">
          <PractitionerIcon className="w-2 h-2 all-child:all-child:fill-mid-grey" />
          <div className="flex flex-col-reverse">
            <div>
              <div className="flex gap-x-0.5">
                <span>Doctor: </span>
                <span>{getDentistWorkingHour(data)}</span>
              </div>

              <div className="flex gap-x-0.5">
                <span>Operatory: </span>
                <span>{data.linkedAppointment.operatory.name}</span>
              </div>
            </div>

            <div className="mb-[15px]">
              <div className="flex gap-x-0.5">
                <span>Hygienist: </span>
                <span>
                  {getPractitionerName(data.doctor)} (
                  {getHygienistWorkingTime(data)})
                </span>
              </div>

              <div className="flex gap-x-0.5">
                <span>Operatory: </span>
                <span>{data.operatory.name}</span>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="flex gap-x-1 flex-col">
        <div className="flex gap-x-1">
          <PractitionerIcon className="w-2 h-2 all-child:all-child:fill-mid-grey" />
          <span>{getPractitionerName(data.doctor)}</span>
        </div>

        <div className="ml-[30px] flex gap-x-0.5">
          <span>Operatory: </span>
          <span>{data.operatory.name}</span>
        </div>
      </div>
    );
  };

  if (rejectAppointmentId) {
    return (
      <RejectAppointmentModal
        appointmentId={rejectAppointmentId}
        onClose={() => setRejectAppointmentId(null)}
        onDone={async () => {
          setRejectAppointmentId(null);
          await matchMutate(/\/appointments\?/);
          if (updateDashboardUI) {
            await updateDashboardUI();
          }
        }}
      />
    );
  }

  return (
    <>
      <Popover.Panel className="absolute z-10 bg-white shadow-primary rounded-[0.8rem] mt-0.6 py-2 px-2 -right-10 w-fit">
        <Popover.Button
          className="text-12 text-darkest-grey hover:text-magenta text-left whitespace-nowrap"
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            setIsModalOpen(true);
          }}
        >
          View Details
        </Popover.Button>
        {appointments.map((appointment) => (
          <Popover.Button
            key={appointment.id}
            className="text-12 text-darkest-grey hover:text-magenta text-left whitespace-nowrap mt-1.2"
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              setRejectAppointmentId(appointment.id);
            }}
          >
            Reject the other appt (
            {datetimeFormat({
              dateString: appointment.appointmentDate,
              pattern: 'MMM DD, YYYY',
            })}
            )
          </Popover.Button>
        ))}
      </Popover.Panel>

      {isModalOpen &&
        createPortal(
          <div onClick={(e: React.MouseEvent) => e.stopPropagation()}>
            <div
              data-testid="modal-overlay"
              className="fixed w-screen h-screen top-0 left-0 bg-opacity-40 bg-[#373737] z-[999]"
              onClick={handleCloseModal}
            />
            <div className="fixed flex flex-col bg-white shadow-2xl round-[10px] top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%] z-[1000] rounded-[1.6rem] min-w-[40rem] max-h-[80vh] p-3 text-darkest-grey text-14 leading-[2.1rem]">
              <section className="flex justify-between items-center shrink-0">
                <h2 className="text-18 font-bold">
                  Existing Appointment Details
                </h2>
                <button onClick={handleCloseModal}>
                  <CloseIcon />
                </button>
              </section>

              <main className="mt-3 flex flex-col gap-y-2.4 pb-3 overflow-y-auto scrollbar">
                {appointments.map((appointment) => {
                  const pmsPatient = (appointment as any)?.pmsPatient;

                  return (
                    <div
                      key={appointment.id}
                      className="border border-light-grey rounded-[0.8rem] p-1.6"
                    >
                      <div className="flex flex-col gap-y-1.2">
                        {pmsPatient && (
                          <div className="flex gap-x-2.4 mb-2">
                            <div className="w-6 h-6 bg-magenta rounded-full flex items-center justify-center text-white text-16 shrink-0">
                              {capitalizeAvatarName(
                                concatFullName({
                                  firstName: pmsPatient.firstName,
                                  lastName: pmsPatient.lastName,
                                })
                              )}
                            </div>
                            <div className="flex flex-col pt-1.2">
                              <span className="text-18 leading-[2.7rem] font-bold">
                                {concatFullName({
                                  firstName: pmsPatient.firstName,
                                  lastName: pmsPatient.lastName,
                                })}
                                {pmsPatient.dob
                                  ? `, ${calculateAge(pmsPatient.dob, 'MMM DD, YYYY')}`
                                  : ''}
                              </span>
                              <span className="text-12 leading-[1.8rem]">
                                Requested on:{' '}
                                {moment(appointment.createdAt).format(
                                  'MMM DD, YYYY hh:mm A'
                                )}
                              </span>
                            </div>
                          </div>
                        )}

                        <div className="grid grid-cols-2 gap-x-1.6 gap-y-1">
                          {pmsPatient && (
                            <h3 className="uppercase text-11 leading-[2.1rem] font-bold text-dim-grey">
                              Patient Info
                            </h3>
                          )}
                          <h3 className="uppercase text-11 leading-[2.1rem] font-bold text-dim-grey">
                            Booking Details
                          </h3>

                          {pmsPatient && (
                            <div className="flex flex-col gap-y-0.8">
                              <div className="flex gap-x-1 items-center">
                                <MailOutlineIcon className="w-2 h-2 all-child:all-child:fill-mid-grey" />
                                <span>{pmsPatient.email || '-'}</span>
                              </div>
                              <div className="flex gap-x-1 items-center">
                                <PhoneIphoneIcon className="w-2 h-2 all-child:all-child:fill-mid-grey" />
                                <span>
                                  {pmsPatient.phoneNumber
                                    ? formatFullPhoneNumber(
                                        `+${pmsPatient.phoneNumber}`
                                      )
                                    : '-'}
                                </span>
                              </div>
                              <div className="flex gap-x-1 items-center">
                                <BirthdayIcon className="w-2 h-2 all-child:all-child:fill-mid-grey" />
                                <span>
                                  {pmsPatient.dob
                                    ? datetimeFormat({
                                        dateString: pmsPatient.dob,
                                        pattern: 'MMM DD, YYYY',
                                      })
                                    : '-'}
                                </span>
                              </div>
                            </div>
                          )}

                          <div className="flex flex-col gap-y-1">
                            <div className="flex gap-x-1 items-center">
                              <TimeIcon className="w-2 h-2 all-child:fill-mid-grey" />
                              <span>
                                {datetimeFormat({
                                  dateString: appointment.appointmentDate,
                                  pattern: 'MMM DD, YYYY',
                                })}{' '}
                                {getAppointmentStartTime(appointment)}
                              </span>
                            </div>

                            <div className="flex gap-x-1 items-center">
                              <ServiceIcon className="w-2 h-2 all-child:fill-mid-grey" />
                              <span>{appointment.service?.name || '-'}</span>
                            </div>

                            {renderDentistHygienistInfo(appointment)}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </main>
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default RebookingPopover;
